import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";

const ConfirmContext = createContext(null);

/**
 * <ConfirmProvider> — wrap your app once. Use `useConfirm()` to call:
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: "Delete this sheet?",
 *     message: 'Norepinephrine will be removed. This cannot be undone.',
 *     confirmLabel: "Delete",
 *     tone: "danger",
 *   });
 *   if (!ok) return;
 *
 * Replaces window.confirm(), which is silently blocked inside iframes and
 * sandboxed webviews (Emergent preview shell, some mobile browsers, etc.).
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        title: opts.title || "Are you sure?",
        message: opts.message || "",
        confirmLabel: opts.confirmLabel || "Confirm",
        cancelLabel: opts.cancelLabel || "Cancel",
        tone: opts.tone || "primary", // 'primary' | 'danger'
      });
    });
  }, []);

  const settle = (result) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    if (resolve) resolve(result);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div
          className="fixed inset-0 bg-black/60 z-[70] grid place-items-center px-4"
          onClick={() => settle(false)}
          data-testid="confirm-modal"
        >
          <div
            className="bg-[#fbf8f2] border border-[#d8ceb8] rounded-sm w-full max-w-md p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className={`shrink-0 rounded-full p-2 ${state.tone === "danger" ? "bg-red-100 text-[#6f2a30]" : "bg-[#f1eee7] text-[#1a1612]"}`}>
                <AlertTriangle className="h-4 w-4" strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-black tracking-tight text-[#1a1612]">{state.title}</h3>
                {state.message && (
                  <p className="text-sm text-[#3a322b] mt-1 leading-relaxed">{state.message}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => settle(false)}
                data-testid="confirm-cancel"
                className="px-4 py-2 border border-[#d8ceb8] text-[#1a1612] text-xs uppercase tracking-widest font-medium rounded-sm hover:bg-[#f1eee7] transition-colors"
              >
                {state.cancelLabel}
              </button>
              <button
                onClick={() => settle(true)}
                data-testid="confirm-ok"
                autoFocus
                className={`px-4 py-2 text-[#f1eee7] text-xs uppercase tracking-widest font-medium rounded-sm transition-colors ${
                  state.tone === "danger" ? "bg-[#6f2a30] hover:bg-[#582227]" : "bg-[#1a1612] hover:bg-[#3a322b]"
                }`}
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback so pages that forgot the provider still work (uses window.confirm — degrades)
    return async (opts) => window.confirm(`${opts?.title || "Confirm?"}\n\n${opts?.message || ""}`);
  }
  return ctx;
}
